import React from 'react';
import { Table, Form, Modal } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat, capitalize } from '../helpers/GlobalHelper';
import { Link, useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';
import GetPagePermission from './GetPagePermission';


interface DayBookRow {
    idAccVouchers: number;
    voucherNo: string;
    voucherType: string;
    voucherDate: string;
    ledgerName: string;
    ledgerAmount: number;
    ledgerDrCr: 'dr' | 'cr';
    narration: string;
}

interface DayBookReportProps {}

const defaultProjectOption: ReportSelectOption = { label: 'Select Project', value: '' };
const DayBookReport: React.FC<DayBookReportProps> = () => {

    const hasDayBookImportPermission = GetPagePermission("/day-book-import");    
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<DayBookRow[]>([]);
    const [importModalShow, setImportModalShow] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [projects, setProjects] = useState<ReportSelectOption[]>([]);
    const [project, setProject] = useState<ReportSelectOption>(defaultProjectOption);

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const hasInitialQuery = useMemo(() => {
        return Boolean(query.get('idProjects') && query.get('fromDate') && query.get('toDate'));
    }, [query]);

    const autoLoadTriggeredRef = useRef(false);

    useEffect(() => {
        getProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectFromQuery = Number(query.get('idProjects'));
            const projectOption = projectFromQuery
                ? projects.find((opt) => Number(opt.value) === projectFromQuery)
                : projects[0];

            setProject(projectOption ?? defaultProjectOption);
            setFormData((prev) => ({
                ...prev,
                idProjects: projectOption && projectOption.value !== ''
                    ? String(projectOption.value)
                    : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.idProjects, formData.fromDate, formData.toDate, hasInitialQuery]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ReportSelectOption[] = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const handleProjectChange = (selectedProject: ReportSelectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects || !formData.fromDate || !formData.toDate) {
            toast.error('Project, From Date, and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/day-book',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setRows(data.data || []);
            } else {
                setRows([]);
                data.errors?.forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report. Please try again later.', {
                position: 'bottom-right',
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    const totalDrAmount = useMemo(
        () => rows.filter((row) => row.ledgerDrCr === 'dr').reduce((total, row) => total + Number(row.ledgerAmount || 0), 0),
        [rows]
    );

    const totalCrAmount = useMemo(
        () => rows.filter((row) => row.ledgerDrCr === 'cr').reduce((total, row) => total + Number(row.ledgerAmount || 0), 0),
        [rows]
    );

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        return params;
    }, [formData]);

    const handleImportClick = () => {
        setImportModalShow(true);
    };

    const handleCloseImportModal = () => {
        setImportModalShow(false);
        setSelectedFile(null);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const fileName = file.name.toLowerCase();
            const allowedExtensions = ['.xlsx', '.xls', '.csv'];
            const isValidFile = allowedExtensions.some(ext => fileName.endsWith(ext));
            
            if (!isValidFile) {
                toast.error('Only XLS, XLSX, or CSV files are allowed.', { position: 'bottom-right', autoClose: 4000 });
                event.target.value = '';
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleDownloadSample = () => {
        // Sample file download - update the URL when sample file is available
        const sampleUrl = `${process.env.REACT_APP_API_URL}reports/day-book-sample`;
        window.open(sampleUrl, '_blank');
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title></Card.Title>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name='fromDate'
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a
                                        className="btn btn-sm btn-primary p-3 mb-2 me-2"
                                        href={`${process.env.REACT_APP_BACKEND}day_book_report_download?` + queryParams.toString()}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <i className='fas fa-download'></i> Download
                                    </a>

                                    {hasDayBookImportPermission ? (
                                        <button
                                            className="btn btn-sm btn-success p-3 mb-2"
                                            onClick={handleImportClick}
                                        >
                                            <i className='fas fa-upload'></i> Import
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12 trial-balance-container table-responsive'>
                    <Table bordered hover>
                        <thead className="sticky-top bg-white" style={{ zIndex: 0 }}>
                            <tr>
                                <th>SL.</th>
                                <th>Voucher Date</th>
                                <th>Voucher Type Name</th>
                                <th>Voucher Number</th>
                                <th>Ledger Name</th>
                                <th className='text-end'>Ledger Amount</th>
                                <th className='text-center'>Ledger Amount Dr/Cr</th>
                                <th>Voucher Narration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center">No Data Found</td>
                                </tr>
                            )}
                            {rows.map((item, index) => (
                                <tr key={`${item.idAccVouchers}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>{item.voucherDate}</td>
                                    <td>{capitalize(item.voucherType)}</td>
                                    <td>
                                        <Link
                                            target='_blank'
                                            className="btn btn-light-primary btn-sm"
                                            to={`transaction-details/${item.idAccVouchers}`}
                                        >
                                            {item.voucherNo}
                                        </Link>
                                    </td>
                                    <td>{item.ledgerName}</td>
                                    <td className="text-end">{numberFormat(item.ledgerAmount || 0)}</td>
                                    <td className="text-center">
                                        {item.ledgerDrCr === 'dr' ? 'Dr' : 'Cr'}
                                    </td>
                                    <td>{item.narration}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5} className="text-end"><strong>Total</strong></td>
                                <td className="text-end">
                                    <strong>{numberFormat(totalDrAmount + totalCrAmount)}</strong>
                                </td>
                                <td className="text-center">Dr: {numberFormat(totalDrAmount)} | Cr: {numberFormat(totalCrAmount)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </div>

            <Modal show={importModalShow} onHide={handleCloseImportModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Import Day Book Data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <h6 className="mb-3">Instructions:</h6>
                        <ul className="list-unstyled">
                            <li className="mb-2">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                Only XLS, XLSX, or CSV File allowed.
                            </li>
                            <li className="mb-2">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                Files should be in our provided format.
                            </li>
                            <li className="mb-2">
                                <i className="fas fa-check-circle text-success me-2"></i>
                                <button
                                    type="button"
                                    onClick={handleDownloadSample}
                                    className="btn btn-link p-0 text-primary text-decoration-underline border-0"
                                    style={{ textAlign: 'left' }}
                                >
                                    Click to download sample file
                                </button>
                            </li>
                            <li className="mb-2">
                                <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                                All existing vouchers will be deleted within the date range of the import file.
                            </li>
                        </ul>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Select File (XLS/XLSX/CSV) <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFileChange}
                        />
                        {selectedFile && (
                            <Form.Text className="text-muted">
                                Selected file: {selectedFile.name}
                            </Form.Text>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        onClick={handleCloseImportModal}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={async () => {
                            if (!selectedFile) {
                                toast.error('Please select a file.', { position: 'bottom-right', autoClose: 4000 });
                                return;
                            }

                            if (!formData.idProjects) {
                                toast.error('Please select a project before importing.', { position: 'bottom-right', autoClose: 4000 });
                                return;
                            }

                            setLoading(true);
                            try {
                                const uploadFormData = new FormData();
                                uploadFormData.append('file', selectedFile);
                                uploadFormData.append('idProjects', formData.idProjects);

                                const token = JSON.parse(localStorage.getItem('MyToken') || 'null');
                                const headers: HeadersInit = {
                                    'Accept': 'application/json',
                                };
                                if (token) {
                                    headers['Authorization'] = `Bearer ${token}`;
                                }
                                // Don't set Content-Type for FormData - browser will set it with boundary

                                const response = await fetch(
                                    `${process.env.REACT_APP_API_URL}reports/day-book-import`,
                                    {
                                        method: 'POST',
                                        headers: headers,
                                        body: uploadFormData,
                                    }
                                );

                                const data = await response.json();

                                if (data.success) {
                                    toast.success(data.message || 'File imported successfully!', { position: 'bottom-right', autoClose: 4000 });
                                    handleCloseImportModal();
                                    // Optionally refresh the report or show the grouped data
                                    console.log('Grouped Data:', data.data.groupedData);
                                } else {
                                    const errors = data.errors || [data.error] || ['Import failed'];
                                    errors.forEach((error: string) =>
                                        toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                                    );
                                }
                            } catch (error) {
                                console.error('Error importing file:', error);
                                toast.error('Failed to import file. Please try again later.', {
                                    position: 'bottom-right',
                                    autoClose: 4000,
                                });
                            } finally {
                                setLoading(false);
                            }
                        }}
                        disabled={!selectedFile || loading}
                    >
                        {loading ? 'Importing...' : 'Import'}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DayBookReport;